// frontend/src/screens/admin/sps/CspRatesScreen.jsx
import { useState } from 'react';
import {
  Container, Row, Col, Table, Button, Badge, Modal,
  Form, Alert, Spinner, Accordion, Card,
} from 'react-bootstrap';
import {
  useGetCspYearsQuery,
  useCreateCspYearMutation,
  useUpdateCspYearMutation,
  useExportCspJsonMutation,
} from '../../../slices/spsApiSlices';

const fmt = (v) => (v != null ? `€${Number(v).toFixed(2)}` : '—');

// ── Period row editor ─────────────────────────────────────────────────────────
function PeriodEditor({ period, index, onChange, onRemove }) {
  const upd = (field, val) => onChange(index, { ...period, [field]: val });

  return (
    <Card className="mb-2 border">
      <Card.Body className="p-2">
        <Row className="g-2 align-items-end">
          <Col xs={12}>
            <Form.Label className="small fw-semibold mb-1">Period label</Form.Label>
            <Form.Control
              size="sm"
              value={period.label || ''}
              onChange={(e) => upd('label', e.target.value)}
              placeholder="e.g. January 2025 (pro-rata)"
            />
          </Col>
          <Col xs={6} sm={4}>
            <Form.Label className="small fw-semibold mb-1">Weekly CSP (€)</Form.Label>
            <Form.Control
              size="sm" type="number" step="0.01"
              value={period.weeklyCsp || ''}
              onChange={(e) => upd('weeklyCsp', e.target.value)}
            />
          </Col>
          <Col xs={6} sm={4}>
            <Form.Label className="small fw-semibold mb-1">Monthly months</Form.Label>
            <Form.Control
              size="sm"
              value={(period.months || []).join(',')}
              onChange={(e) =>
                upd('months', e.target.value.split(',').map(Number).filter(Boolean))
              }
              placeholder="1,2,3 …12"
            />
          </Col>
          <Col xs={12} sm={4}>
            <Form.Label className="small fw-semibold mb-1">Fortnightly periods</Form.Label>
            <Form.Control
              size="sm"
              value={(period.fortnightlySeq || []).join(',')}
              onChange={(e) =>
                upd('fortnightlySeq', e.target.value.split(',').map(Number).filter(Boolean))
              }
              placeholder="1,2,3 …26"
            />
          </Col>
          <Col xs={6} sm={3}>
            <Form.Label className="small fw-semibold mb-1">Monthly offset (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01"
              value={period.monthlyOffset || ''}
              onChange={(e) => upd('monthlyOffset', e.target.value)} />
          </Col>
          <Col xs={6} sm={3}>
            <Form.Label className="small fw-semibold mb-1">Monthly threshold (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01"
              value={period.monthlyThreshold || ''}
              onChange={(e) => upd('monthlyThreshold', e.target.value)} />
          </Col>
          <Col xs={6} sm={3}>
            <Form.Label className="small fw-semibold mb-1">Fortnightly offset (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01"
              value={period.fortnightlyOffset || ''}
              onChange={(e) => upd('fortnightlyOffset', e.target.value)} />
          </Col>
          <Col xs={6} sm={3}>
            <Form.Label className="small fw-semibold mb-1">Fortnightly threshold (€)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01"
              value={period.fortnightlyThreshold || ''}
              onChange={(e) => upd('fortnightlyThreshold', e.target.value)} />
          </Col>
          <Col xs={12}>
            <Form.Label className="small fw-semibold mb-1">Note</Form.Label>
            <Form.Control size="sm"
              value={period.note || ''}
              onChange={(e) => upd('note', e.target.value)}
              placeholder="Optional note" />
          </Col>
          <Col xs={12} className="text-end">
            <Button size="sm" variant="outline-danger" onClick={() => onRemove(index)}>
              Remove period
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

const emptyPeriod = {
  label: '', months: [], fortnightlySeq: [],
  weeklyCsp: '', monthlyOffset: '', monthlyThreshold: '',
  fortnightlyOffset: '', fortnightlyThreshold: '', note: '',
};

const emptyForm = { year: '', label: '', periods: [{ ...emptyPeriod }] };

// ── Main screen ───────────────────────────────────────────────────────────────
export default function CspRatesScreen() {
  const { data: years = [], isLoading, isError, refetch } = useGetCspYearsQuery();
  const [createCsp, { isLoading: creating }] = useCreateCspYearMutation();
  const [updateCsp, { isLoading: updating }] = useUpdateCspYearMutation();
  const [exportCsp, { isLoading: exporting }] = useExportCspJsonMutation();

  const [showModal, setShowModal] = useState(false);
  const [editYear,  setEditYear]  = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [feedback,  setFeedback]  = useState(null);

  const openCreate = () => {
    setEditYear(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditYear(row.year);
    setForm({
      year:    row.year,
      label:   row.label || '',
      periods: row.periods.map((p) => ({
        label:                p.label || '',
        months:               p.months || [],
        fortnightlySeq:       p.fortnightlySeq || [],
        weeklyCsp:            p.weeklyCsp || '',
        monthlyOffset:        p.monthlyOffset || '',
        monthlyThreshold:     p.monthlyThreshold || '',
        fortnightlyOffset:    p.fortnightlyOffset || '',
        fortnightlyThreshold: p.fortnightlyThreshold || '',
        note:                 p.note || '',
      })),
    });
    setShowModal(true);
  };

  const updatePeriod = (idx, updated) => {
    const ps = [...form.periods];
    ps[idx] = updated;
    setForm({ ...form, periods: ps });
  };

  const removePeriod = (idx) =>
    setForm({ ...form, periods: form.periods.filter((_, i) => i !== idx) });

  const addPeriod = () =>
    setForm({ ...form, periods: [...form.periods, { ...emptyPeriod }] });

  const handleSave = async () => {
    const payload = {
      label:   form.label,
      periods: form.periods.map((p) => ({
        ...p,
        weeklyCsp:            Number(p.weeklyCsp),
        monthlyOffset:        p.monthlyOffset        ? Number(p.monthlyOffset)        : undefined,
        monthlyThreshold:     p.monthlyThreshold     ? Number(p.monthlyThreshold)     : undefined,
        fortnightlyOffset:    p.fortnightlyOffset    ? Number(p.fortnightlyOffset)    : undefined,
        fortnightlyThreshold: p.fortnightlyThreshold ? Number(p.fortnightlyThreshold) : undefined,
      })),
    };
    try {
      if (editYear !== null) {
        await updateCsp({ year: editYear, ...payload }).unwrap();
        setFeedback({ type: 'success', msg: `Year ${editYear} updated` });
      } else {
        await createCsp({ year: Number(form.year), ...payload }).unwrap();
        setFeedback({ type: 'success', msg: `Year ${form.year} created` });
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Save failed' });
    }
  };

  const handleExport = async () => {
    try {
      const r = await exportCsp().unwrap();
      setFeedback({ type: 'success', msg: `✓ ${r.message} (${r.years} years)` });
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Export failed' });
    }
  };

  if (isLoading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  if (isError)   return <Alert variant="danger" className="m-3">Failed to load CSP assumptions.</Alert>;

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">CSP Assumptions</h4>
          <small className="text-muted">
            Contributory State Pension rate parameters used to calculate contribution
            offsets and referable amount thresholds for each year
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <Spinner size="sm" animation="border" /> : '⬇ Export to JSON'}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>+ Add Year</Button>
        </Col>
      </Row>

      {feedback && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)} className="mb-3">
          {feedback.msg}
        </Alert>
      )}

      <Accordion>
        {years.map((row) => {
          const firstPeriod = row.periods?.[0] || {};
          return (
            <Accordion.Item eventKey={String(row.year)} key={row.year}>
              <Accordion.Header>
                <span className="fw-bold me-3">{row.year}</span>
                <span className="text-muted me-3" style={{ fontSize: '.85rem' }}>
                  Weekly CSP: <strong>€{firstPeriod.weeklyCsp}</strong>
                </span>
                <Badge bg="secondary" className="me-3">{row.periods?.length} period{row.periods?.length !== 1 ? 's' : ''}</Badge>
                <span className="text-muted" style={{ fontSize: '.8rem' }}>{row.label}</span>
              </Accordion.Header>
              <Accordion.Body>
                <Table size="sm" bordered className="mb-3">
                  <thead className="table-light">
                    <tr>
                      <th>Period label</th>
                      <th>Weekly CSP</th>
                      <th>Monthly offset</th>
                      <th>Monthly threshold</th>
                      <th>FN offset</th>
                      <th>FN threshold</th>
                      <th>Months</th>
                      <th>FN periods</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.periods.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '.8rem' }}>{p.label}</td>
                        <td>{fmt(p.weeklyCsp)}</td>
                        <td>{fmt(p.monthlyOffset)}</td>
                        <td>{fmt(p.monthlyThreshold)}</td>
                        <td>{fmt(p.fortnightlyOffset)}</td>
                        <td>{fmt(p.fortnightlyThreshold)}</td>
                        <td style={{ fontSize: '.75rem' }}>{(p.months || []).join(', ')}</td>
                        <td style={{ fontSize: '.75rem' }}>{(p.fortnightlySeq || []).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Button size="sm" variant="outline-primary" onClick={() => openEdit(row)}>
                  Edit Year {row.year}
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Edit / Create Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editYear !== null ? `Edit CSP Year ${editYear}` : 'Add New CSP Year'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editYear === null && (
            <Form.Group className="mb-3">
              <Form.Label>Year <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number" min={2013} max={2100}
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                placeholder="e.g. 2026"
                style={{ maxWidth: 160 }}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Year label / description</Form.Label>
            <Form.Control
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. 2025 assumptions (CSP rate change from 3 Jan)"
            />
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Pay periods ({form.periods.length})</strong>
            <Button size="sm" variant="outline-success" onClick={addPeriod}>+ Add Period</Button>
          </div>

          {form.periods.map((p, i) => (
            <PeriodEditor
              key={i} period={p} index={i}
              onChange={updatePeriod}
              onRemove={removePeriod}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={creating || updating || form.periods.length === 0}
          >
            {creating || updating ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}